import {AUTO_SELECT_ID} from 'shared/lib/constants';

import React, {useRef} from 'react';
import {useSelector} from 'react-redux';
import {Control, Controller, UseFormSetValue, useWatch} from 'react-hook-form';
import {t} from '@lingui/core/macro';

import {locationAPI, ILocation} from 'entities/location';
import {HintPremium} from 'entities/tariff';
import GeoIntervalSlider from 'shared/ui/Slider/GeoIntervalSlider';
import HintQuestion from 'shared/ui/Hint/HintQuestion';
import MultiSelectWithCheckbox, {
    MultiSelectHandle,
    OptionNode,
} from 'shared/ui/Input/MultiSelectWithCheckbox/MultiSelectWithCheckbox';
import Copying from 'shared/ui/icons/Copying';
import {useCopyToClipboard} from 'shared/lib/hooks/useCopyToClipboard';
import {useIsFreeTariff, userSelector} from 'entities/user';

import styles from './GeoCheck.module.scss';

// Приоритет зон в выпадающем списке: сначала Россия, затем СНГ, остальные — по алфавиту.
const ZONE_PRIORITY: Record<string, number> = {
    RU: 0,
    CIS: 1,
};

interface IGeoCheckProps {
    item?: {threshold?: number};
    control: Control<any>;
    setValue: UseFormSetValue<any>;
    showLocations?: boolean;
    showThreshold?: boolean;
}

const GeoCheck: React.FC<IGeoCheckProps> = ({
    control,
    item,
    setValue,
    showLocations = true,
    showThreshold = true,
}) => {
    const user = useSelector(userSelector);
    const freeTariff = useIsFreeTariff();
    const locationMaxCount = user?.tariff.location_max_count_limit;
    const { copy, isCopied } = useCopyToClipboard();

    const selectRef = useRef<MultiSelectHandle>(null);

    const {data: locationsData} = locationAPI.useGetLocationsQuery(
        {
            page: 1,
        },
    );

    const selectedLocations = useWatch({
        control,
        name: 'locations',
        defaultValue: [],
    });

    const locationOptions = React.useMemo((): OptionNode[] => {
        const autoSelectOption: OptionNode = {
            id: AUTO_SELECT_ID,
            value: AUTO_SELECT_ID,
            label: t`Автовыбор`,
            hidden: !freeTariff,
        };

        if (!locationsData?.items) return [autoSelectOption];

        // Группируем локации по зонам. Порядок зон: Россия, СНГ, остальные по алфавиту.
        const zoneGroups = new Map<string, { name: string; children: OptionNode[] }>();
        locationsData.items.forEach((location: ILocation) => {
            const zone = location.city.country.zone;
            const group = zoneGroups.get(zone.code)
                ?? { name: zone.name, children: [] };
            group.children.push({
                id: location.id.toString(),
                value: location.id.toString(),
                label: `${location.city.country.name}, ${location.city.name}, ${location.name}`,
            });
            zoneGroups.set(zone.code, group);
        });

        const groupOptions = Array.from(zoneGroups.entries())
            .sort(([codeA, a], [codeB, b]) => {
                const priorityA = ZONE_PRIORITY[codeA] ?? Infinity;
                const priorityB = ZONE_PRIORITY[codeB] ?? Infinity;
                if (priorityA !== priorityB) return priorityA - priorityB;
                return a.name.localeCompare(b.name, 'ru');
            })
            .map(([code, group]): OptionNode => ({
                id: `zone-${code}`,
                value: `zone-${code}`,
                label: group.name,
                isGroup: true,
                children: group.children.sort((x, y) => x.label.localeCompare(y.label, 'ru')),
            }));

        return [autoSelectOption, ...groupOptions];
    }, [locationsData, freeTariff]);

    const selectedLocationsData = React.useMemo(() => {
        if (!locationsData?.items || !selectedLocations?.length) return [];

        return locationsData.items.filter((location: ILocation) =>
            selectedLocations.includes(location.id.toString()),
        );
    }, [locationsData, selectedLocations]);

    const selectedLocationsWithIp = React.useMemo(() => {
        return selectedLocationsData.filter((location: ILocation) => location.ip !== null);
    }, [selectedLocationsData]);

    const locationsBlock = (
        <>
            <div className={styles.label}>
                {
                    freeTariff &&
                    <HintPremium condition={'monitor_extended_methods'} />
                }
                {t`Локации мониторинга`}
                <HintQuestion placement="top-start" className={styles.icon}>
                    {/* eslint-disable-next-line max-len */}
                    {t`Проверки из разных локаций помогают точнее оценивать доступность сайта и понимать, где именно возникает проблема.`}
                </HintQuestion>
            </div>

            <Controller
                name="locations"
                control={control}
                render={({ field, fieldState }) => {
                    const currentValue: string[] = field.value || [];

                    const handleChange = (newValue: string[]) => {
                        let finalValue = newValue;

                        const addedRealItem = newValue.some(
                            v => v !== AUTO_SELECT_ID && !currentValue.includes(v),
                        );
                        if (newValue.includes(AUTO_SELECT_ID) && addedRealItem) {
                            finalValue = newValue.filter(v => v !== AUTO_SELECT_ID);
                        }

                        field.onChange(finalValue);

                        const autoSelectRemoved = currentValue.includes(AUTO_SELECT_ID)
                            && !newValue.includes(AUTO_SELECT_ID);
                        if (autoSelectRemoved) {
                            selectRef.current?.openDropdown();
                        }
                    };

                    const handleClose = () => {
                        if (!field.value || field.value.length === 0) {
                            field.onChange([AUTO_SELECT_ID]);
                        }
                    };

                    return (
                        <MultiSelectWithCheckbox
                            ref={selectRef}
                            options={locationOptions}
                            value={freeTariff ? [AUTO_SELECT_ID] : currentValue}
                            onChange={freeTariff ? () => { /* empty */ } : handleChange}
                            onClose={freeTariff ? undefined : handleClose}
                            placeholder={t`Выберите локации...`}
                            name={field.name}
                            fieldState={fieldState}
                            maxCount={locationMaxCount}
                            isFreeTariff={freeTariff}
                        />
                    );
                }}
            />
            {
                (selectedLocationsWithIp.length > 0) &&
                            <div className={styles.containerIP}>
                                <div className={styles.action}>
                                    <span
                                        className={styles.icon}
                                        onClick={() => {
                                            const ipsText = selectedLocationsWithIp.map(item => item.ip).join('\n');
                                            void copy(ipsText);
                                        }}
                                    >
                                        <Copying />
                                        {isCopied && <span className={styles.tooltip}>{t`Скопировано`}</span>}
                                    </span>
                                    <span>{t`Скопировать IP-адреса для белого списка`}</span>
                                    <HintQuestion placement="top-start">
                                        {/* eslint-disable-next-line max-len */}
                                        {t`Скопируйте эти IP-адреса и добавьте их в белый список на стороне сервера, хостинга или firewall, чтобы проверки не блокировались.`}
                                    </HintQuestion>
                                </div>
                                <div className={styles.content}>
                                    {selectedLocationsWithIp.map((item) => (
                                        <span key={item.id} className={styles.item}>{item.ip}</span>
                                    ))}
                                </div>
                            </div>
            }
        </>
    );

    const thresholdBlock = (
        <div className={styles.sliderBlock}>
            <div className={styles.label}>
                {
                    freeTariff &&
                    <HintPremium condition={'monitor_extended_methods'} />
                }
                {t`Порог срабатывания`}
                <HintQuestion
                    className={styles.icon}
                    placement="top-start"
                >
                    {t`Выберите, из скольких локаций сайт должен быть недоступен, чтобы открыть инцидент`}
                </HintQuestion>
            </div>
            <GeoIntervalSlider
                name="threshold"
                setValue={setValue}
                minimalValue={1}
                maxValue={selectedLocations.length || 1}
                defaultValue={item?.threshold ?? (selectedLocations.length || 1)}
            />
        </div>
    );

    return (
        <div className={styles.container}>
            {showLocations && locationsBlock}
            {showThreshold && thresholdBlock}
        </div>
    );
};

export default GeoCheck;
